import React from 'react'
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Snackbar,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Checkbox,
  TableContainer,
  Autocomplete,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import MuiAlert from '@mui/material/Alert'
import API from '@aws-amplify/api'
import { useDispatch, useSelector } from 'react-redux'
import { IReduxState } from 'interfaces/IReduxState'
import { VALUE_IN, VALUE_OUT, VALUE_BARANG_JADI } from 'enums/stockCard'
import {
  BarangJadiRequest,
  BARANG_JADI_IN_OPTIONS,
  BARANG_JADI_OUT_OPTIONS,
  PERLU_TINDAKAN_OPTIONS,
  IBarangJadiRequest,
} from 'interfaces/IBarangJadiRequest'
import { IBrand } from 'interfaces/IBrand'
import { IProduct } from 'interfaces/IProduct'
import LoadBrandAutocomplete from 'shared/autocomplete/loadBrandAutocomplete'
import LoadProductAutocomplete from 'shared/autocomplete/loadProductAutocomplete'
import { formatQuantity, formatDecimalAuto } from 'shared/numberFormatter'

interface IProductBatch {
  batchNumber: string
  premixId: string | null
  expiryDate: string | null
  productionDate: string | null
  currentQuantity: number
  productStock: number
  isExpired: boolean
}

interface BetEntry {
  qty: string
  batchNumber: string
}

function boolIcon(val: boolean | undefined) {
  return <Checkbox disabled checked={!!val} />
}

const todayString = () => new Date().toISOString().slice(0, 10)

export default function BarangJadiRequestForm() {
  const user = useSelector((state: IReduxState) => state.currentUser || {})
  const dispatch = useDispatch()

  const [request, setRequest] = React.useState<IBarangJadiRequest>(
    new BarangJadiRequest()
  )
  const [brandId, setBrandId] = React.useState<string>('')
  const [product, setProduct] = React.useState<Partial<IProduct> | null>(null)
  const [bets, setBets] = React.useState<BetEntry[]>([{ qty: '', batchNumber: '' }])
  const [productBatches, setProductBatches] = React.useState<IProductBatch[]>([])
  const [isLoadingBatches, setIsLoadingBatches] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isSuccessOpen, setIsSuccessOpen] = React.useState(false)
  const [isFailureOpen, setIsFailureOpen] = React.useState(false)
  const [perluTindakanSubOptions, setPerluTindakanSubOptions] = React.useState<string[]>([])

  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Request Barang Jadi In/Out' })
  }, [dispatch])

  const isOut = request.inOutDirection === VALUE_OUT

  const loadProductBatches = React.useCallback(async (productId: number) => {
    setIsLoadingBatches(true)
    try {
      const response: IProductBatch[] = await API.get(
        'products',
        '/products/getProductBatches',
        { queryStringParameters: { productId } }
      )
      setProductBatches(response || [])
    } catch {
      setProductBatches([])
    } finally {
      setIsLoadingBatches(false)
    }
  }, [])

  React.useEffect(() => {
    if (product?.productId) {
      loadProductBatches(product.productId as number)
    } else {
      setProductBatches([])
    }
  }, [product, loadProductBatches])


  const handleInOutChange = (value: string) => {
    setRequest((prev) => ({
      ...prev,
      inOutDirection: value,
      sourceOrDestination: '',
    }))
    setBets([{ qty: '', batchNumber: '' }])
    setPerluTindakanSubOptions([])
  }

  const handleSourceChange = (value: string) => {
    if (value === 'Perlu tindakan') {
      setRequest((prev) => ({
        ...prev,
        sourceOrDestination: 'Perlu tindakan: ',
      }))
      setPerluTindakanSubOptions([])
    } else if (value === 'Selesai tindakan, return ke customer') {
      setRequest((prev) => ({
        ...prev,
        sourceOrDestination: 'Selesai tindakan, return ke customer: ',
      }))
      setPerluTindakanSubOptions([])
    } else {
      setRequest((prev) => ({
        ...prev,
        sourceOrDestination: value,
      }))
      setPerluTindakanSubOptions([])
    }
  }

  const handlePerluTindakanToggle = (opt: string) => {
    setPerluTindakanSubOptions((prev) => {
      const next = prev.includes(opt)
        ? prev.filter((o) => o !== opt)
        : [...prev, opt]

      const prefix =
        request.inOutDirection === VALUE_IN
          ? 'Perlu tindakan: '
          : 'Selesai tindakan, return ke customer: '

      const concatenated =
        next.length > 0 ? `${prefix}${next.join(', ')}` : prefix

      setRequest((prevReq) => ({
        ...prevReq,
        sourceOrDestination: concatenated,
      }))

      return next
    })
  }

  const handleBrandChange = (b: IBrand) => {
    setBrandId(b?.brandId ?? '')
    setProduct(null)
    setBets([{ qty: '', batchNumber: '' }])
  }

  const handleProductChange = (p: IProduct) => {
    setProduct(p && p.productId ? p : null)
    setBets([{ qty: '', batchNumber: '' }])
  }

  const updateBet = (idx: number, field: keyof BetEntry, value: string) => {
    setBets((prev) => prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)))
  }

  const addBet = () => setBets((prev) => [...prev, { qty: '', batchNumber: '' }])

  const removeBet = (idx: number) =>
    setBets((prev) => prev.filter((_, i) => i !== idx))

  const batchOptionsForBet = (idx: number): IProductBatch[] => {
    const usedElsewhere = new Set(
      bets.filter((_, i) => i !== idx).map((b) => b.batchNumber).filter(Boolean)
    )
    let pool = productBatches
    if (isOut) {
      pool = pool.filter((b) => Number(b.productStock) > 0)
    }
    return pool.filter((b) => !usedElsewhere.has(b.batchNumber))
  }

  const stockForBatch = (batchNumber: string): number => {
    const found = productBatches.find((b) => b.batchNumber === batchNumber)
    return found ? Number(found.productStock || 0) : 0
  }

  const getMissingFields = (): string[] => {
    const missing: string[] = []
    if (!request.inOutDirection) missing.push('In/Out')
    if (!request.sourceOrDestination) missing.push('Dari/Tujuan')
    if (
      request.sourceOrDestination === 'Perlu tindakan: ' ||
      request.sourceOrDestination === 'Selesai tindakan, return ke customer: '
    )
      missing.push('Pilih minimal satu tindakan')
    if (!product?.productId) missing.push('Pilih Barang Jadi')

    if (product?.productId) {
      if (bets.length === 0) {
        missing.push('Tambah minimal satu bets')
      }
      bets.forEach((b, i) => {
        const idx = i + 1
        if (!b.batchNumber) missing.push(`Nomor Bets baris #${idx}`)
        if (!b.qty || Number(b.qty) <= 0) missing.push(`Qty baris #${idx}`)
        if (
          isOut &&
          b.batchNumber &&
          Number(b.qty) > stockForBatch(b.batchNumber)
        ) {
          missing.push(`Qty baris #${idx} melebihi stok`)
        }
      })
    }

    return missing
  }

  const isFormValid = () => getMissingFields().length === 0

  const handleSave = async () => {
    if (!product || !product.productId) return
    setIsLoading(true)
    try {
      const payload = {
        ...request,
        items: bets.map((b) => ({
          productId: product.productId,
          productName: product.productName,
          packagingId: product.packagingId,
          premixId: product.premixId,
          fillingAmount: (product as any).fillingAmount ?? null,
          fillingUnit:
            (product as any).fillingUnit ||
            (product as any).unit ||
            '',
          labelId: (product as any).labelId || null,
          batchNumber: b.batchNumber,
          qty: Number(b.qty),
        })),
      }

      await API.post('products', '/products/barangJadiRequest', { body: payload })
      setAlertMessage('Berhasil membuat request barang jadi.')
      setIsSuccessOpen(true)
      setRequest(new BarangJadiRequest())
      setBrandId('')
      setProduct(null)
      setBets([{ qty: '', batchNumber: '' }])
      setPerluTindakanSubOptions([])
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Gagal membuat request barang jadi.'
      setAlertMessage(Array.isArray(msg) ? msg.join(', ') : msg)
      setIsFailureOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  const productInfoCells = [
    { label: 'ID Produk', value: product?.productId },
    { label: 'Nama', value: product?.productName },
    { label: 'Kode Kemasan', value: product?.packagingId },
    { label: 'Kode Ruahan', value: product?.premixId },
    {
      label: 'Filling',
      value:
        (product as any)?.fillingAmount != null
          ? `${formatDecimalAuto((product as any).fillingAmount)}${
              (product as any).fillingUnit
                ? ` ${(product as any).fillingUnit}`
                : ''
            }`
          : '-',
    },
    { label: 'Merek', value: product?.brandId },
    { label: 'Label ID', value: (product as any)?.labelId || '-' },
    { label: 'Notif #', value: (product as any)?.notificationNumber || '-' },
  ]

  const computedSourceOptions =
    request.inOutDirection === VALUE_IN
      ? BARANG_JADI_IN_OPTIONS
      : request.inOutDirection === VALUE_OUT
      ? BARANG_JADI_OUT_OPTIONS
      : []

  return (
    <React.Fragment>
      <Paper>
        <Box p={3}>
          <List>
            {/* In/Out */}
            <ListItem>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography>
                    <Box fontWeight="bold" display="inline">
                      In/Out
                    </Box>
                  </Typography>
                </FormLabel>
                <RadioGroup
                  row
                  value={request.inOutDirection}
                  onChange={(e) => handleInOutChange(e.target.value)}
                >
                  <FormControlLabel
                    value={VALUE_IN}
                    control={<Radio color="primary" />}
                    label="Masuk ke Gudang Jadi"
                  />
                  <FormControlLabel
                    value={VALUE_OUT}
                    control={<Radio color="primary" />}
                    label="Keluar ke Gudang Jadi"
                  />
                </RadioGroup>
              </FormControl>
            </ListItem>

            {/* Dari/Tujuan */}
            {request.inOutDirection && (
              <ListItem>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    <Typography>
                      <Box fontWeight="bold" display="inline">
                        {request.inOutDirection === VALUE_IN ? 'Dari' : 'Tujuan'}
                      </Box>
                    </Typography>
                  </FormLabel>
                  <RadioGroup
                    value={
                      request.sourceOrDestination.startsWith('Perlu tindakan')
                        ? 'Perlu tindakan'
                        : request.sourceOrDestination.startsWith(
                            'Selesai tindakan'
                          )
                        ? 'Selesai tindakan, return ke customer'
                        : request.sourceOrDestination
                    }
                    onChange={(e) => handleSourceChange(e.target.value)}
                  >
                    {computedSourceOptions.map((opt: any) => (
                      <FormControlLabel
                        key={opt.key}
                        value={opt.key}
                        control={<Radio color="primary" />}
                        label={opt.label}
                      />
                    ))}
                  </RadioGroup>
                  {(request.sourceOrDestination.startsWith('Perlu tindakan') ||
                    request.sourceOrDestination.startsWith(
                      'Selesai tindakan'
                    )) && (
                    <Box ml={4} display="flex" flexDirection="column">
                      {PERLU_TINDAKAN_OPTIONS.map((opt) => (
                        <FormControlLabel
                          key={opt}
                          control={
                            <Checkbox
                              size="small"
                              checked={perluTindakanSubOptions.includes(opt)}
                              onChange={() => handlePerluTindakanToggle(opt)}
                            />
                          }
                          label={opt}
                        />
                      ))}
                    </Box>
                  )}
                </FormControl>
              </ListItem>
            )}

            {/* Brand + Product selection */}
            <ListItem>
              <Box width="100%">
                <Typography variant="subtitle1" gutterBottom>
                  <Box fontWeight="bold" display="inline">
                    Pilih Barang Jadi untuk di masukan/keluarkan
                  </Box>
                </Typography>
                <Box mb={2}>
                  <LoadBrandAutocomplete
                    name={brandId}
                    onChange={handleBrandChange}
                    preload
                  />
                </Box>
                <Box>
                  <LoadProductAutocomplete
                    name={product?.productName || ''}
                    brandId={brandId}
                    type={VALUE_BARANG_JADI}
                    onChange={handleProductChange}
                    showProductIdColumn
                  />
                </Box>
              </Box>
            </ListItem>

            {/* Product info table */}
            {product?.productId && (
              <ListItem>
                <Box width="100%">
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {productInfoCells.map((c) => (
                            <TableCell key={c.label}>{c.label}</TableCell>
                          ))}
                          <TableCell>Halal</TableCell>
                          <TableCell>Dus</TableCell>
                          <TableCell>Shrink Wrap</TableCell>
                          <TableCell>Segel</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          {productInfoCells.map((c) => (
                            <TableCell key={c.label}>{String(c.value ?? '-')}</TableCell>
                          ))}
                          <TableCell>{boolIcon(!!product.halal)}</TableCell>
                          <TableCell>{boolIcon(!!product.box)}</TableCell>
                          <TableCell>{boolIcon(!!product.wrap)}</TableCell>
                          <TableCell>{boolIcon(!!product.seal)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </ListItem>
            )}

            {/* Bets entry */}
            {product?.productId && request.inOutDirection && (
              <ListItem>
                <Box width="100%">
                  <Typography variant="subtitle1" gutterBottom>
                    <Box fontWeight="bold" display="inline">
                      {isOut ? 'Bets Mau di Keluarkan' : 'Bets Mau di Masukin'}
                    </Box>
                  </Typography>
                  {isLoadingBatches && (
                    <Typography variant="caption" color="textSecondary">
                      Memuat daftar bets...
                    </Typography>
                  )}
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ width: '20%' }}>Qty</TableCell>
                          <TableCell>No Bets</TableCell>
                          {isOut && <TableCell style={{ width: '15%' }}>Stok</TableCell>}
                          <TableCell padding="none" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bets.map((bet, idx) => {
                          const options = batchOptionsForBet(idx)
                          const stock = isOut ? stockForBatch(bet.batchNumber) : null
                          const overStock =
                            isOut &&
                            bet.batchNumber &&
                            bet.qty !== '' &&
                            Number(bet.qty) > stock!

                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                <TextField
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                  placeholder="Qty"
                                  value={bet.qty}
                                  inputProps={{
                                    min: 1,
                                    step: 1,
                                    ...(isOut && stock != null
                                      ? { max: stock }
                                      : {}),
                                  }}
                                  onChange={(e) =>
                                    updateBet(idx, 'qty', e.target.value)
                                  }
                                  error={!!overStock}
                                  helperText={
                                    overStock
                                      ? `Maks ${formatQuantity(stock!)}`
                                      : ''
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Autocomplete
                                  size="small"
                                  freeSolo={!isOut}
                                  selectOnFocus
                                  clearOnBlur={false}
                                  handleHomeEndKeys
                                  options={options.map((o) => o.batchNumber)}
                                  value={bet.batchNumber || null}
                                  onChange={(_, val) =>
                                    updateBet(idx, 'batchNumber', val || '')
                                  }
                                  onInputChange={(_, val, reason) => {
                                    if (!isOut && reason === 'input') {
                                      updateBet(idx, 'batchNumber', val)
                                    }
                                  }}
                                  renderOption={(props, option) => {
                                    const meta = productBatches.find(
                                      (b) => b.batchNumber === option
                                    )
                                    const tags: string[] = []
                                    if (meta?.isExpired) tags.push('expired')
                                    if (meta && Number(meta.productStock) > 0)
                                      tags.push(
                                        `stok ${formatQuantity(meta.productStock)}`
                                      )
                                    else if (meta) tags.push('stok 0')
                                    const { key, ...rest } = props as any
                                    return (
                                      <li {...rest} key={option}>
                                        {option}
                                        {tags.length > 0 && (
                                          <Box
                                            component="span"
                                            ml={1}
                                            color="text.secondary"
                                          >
                                            ({tags.join(', ')})
                                          </Box>
                                        )}
                                      </li>
                                    )
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder={isOut ? 'Pilih Bets' : 'Ketik atau pilih Bets'}
                                      variant="outlined"
                                    />
                                  )}
                                />
                              </TableCell>
                              {isOut && (
                                <TableCell>
                                  {bet.batchNumber
                                    ? formatQuantity(stock!)
                                    : '-'}
                                </TableCell>
                              )}
                              <TableCell padding="none">
                                {bets.length > 1 && (
                                  <IconButton
                                    size="small"
                                    onClick={() => removeBet(idx)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box mt={1}>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addBet}
                      disabled={
                        isOut &&
                        productBatches.filter(
                          (b) => Number(b.productStock) > 0
                        ).length <= bets.length
                      }
                    >
                      Tambah Bets untuk Barang Jadi ini
                      {isOut ? ' (hanya bets dengan stok)' : ''}
                    </Button>
                  </Box>
                </Box>
              </ListItem>
            )}

            {/* Notes */}
            <ListItem>
              <TextField
                autoComplete="off"
                variant="outlined"
                value={request.notes}
                fullWidth
                label="Notes"
                onChange={(e) =>
                  setRequest((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </ListItem>

            <ListItem>
              <Typography>Request Dibuat Oleh: {user.username}</Typography>
            </ListItem>
            <ListItem>
              <Typography variant="caption" color="textSecondary">
                Tanggal: {todayString()}
              </Typography>
            </ListItem>
          </List>

          {getMissingFields().length > 0 && (
            <Box mt={2}>
              <MuiAlert severity="warning" elevation={0} variant="outlined">
                Lengkapi data berikut sebelum menyimpan:{' '}
                {getMissingFields().join(', ')}
              </MuiAlert>
            </Box>
          )}

          <Box
            p={2}
            mt={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
          >
            <Button
              variant="contained"
              color="primary"
              disabled={!isFormValid() || isLoading}
              onClick={handleSave}
            >
              {isLoading ? 'Menyimpan...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={isSuccessOpen}
        autoHideDuration={6000}
        onClose={() => setIsSuccessOpen(false)}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureOpen}
        autoHideDuration={6000}
        onClose={() => setIsFailureOpen(false)}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  )
}
